import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ThemeToggle } from "~/lib/theme";
import { RssTicker } from "~/components/RssTicker";
import { LanguageSwitcher } from "~/components/LanguageSwitcher";
import { AlchemaLogo } from "~/components/AlchemaLogo";
import { Breadcrumbs } from "./Breadcrumbs";
import { TopBarPricingLink } from "./TopBarPricingLink";
import { icons } from "./SidebarIcons";

interface TopBarProps {
  session: { user?: { name?: string | null } | null } | null;
  onMenuToggle: () => void;
  sidebarOpen: boolean;
  onCmdPaletteOpen?: () => void;
}

// Detect Mac for ⌘ vs Ctrl hint
function useIsMac() {
  const [isMac, setIsMac] = React.useState(false);
  React.useEffect(() => {
    setIsMac(typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/.test(navigator.platform));
  }, []);
  return isMac;
}

export function TopBar({ session, onMenuToggle, sidebarOpen, onCmdPaletteOpen }: TopBarProps) {
  const isMac = useIsMac();
  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b px-4 sm:px-6"
      style={{
        height: "var(--topbar-height, 44px)",
        borderColor: "var(--border-subtle)",
        background: "var(--bg)",
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile menu button */}
        <button
          className="btn-ghost flex h-9 w-9 items-center justify-center rounded-lg md:hidden"
          onClick={onMenuToggle}
          aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          aria-expanded={sidebarOpen}
        >
          <span className="h-5 w-5">
            {sidebarOpen ? icons.close : icons.menu}
          </span>
        </button>

        {/* Mobile-only logo — sidebar is hidden behind the hamburger at < md,
            so the brand needs to live in the topbar there */}
        <Link
          to="/dashboard"
          className="md:hidden flex items-center no-underline"
          aria-label="Alchema dashboard"
        >
          <AlchemaLogo style={{ fontSize: "1.125rem" }} />
        </Link>

        {/* Breadcrumbs (desktop only — mobile shows logo instead) */}
        <div className="hidden md:flex">
          <Breadcrumbs />
        </div>
      </div>

      {/* Command palette trigger — Attio signature, hides RSS ticker on medium screens */}
      {onCmdPaletteOpen ? (
        <button
          type="button"
          onClick={onCmdPaletteOpen}
          className="cmd-trigger hidden md:inline-flex"
          aria-label="Open command palette"
        >
          <span className="flex items-center gap-2">
            <span className="h-3.5 w-3.5" aria-hidden="true">{icons.search}</span>
            <span>Search or jump to…</span>
          </span>
          <span className="kbd" aria-hidden="true">{isMac ? "⌘" : "Ctrl"}K</span>
        </button>
      ) : null}

      {/* RSS Ticker — centered, only shown when no palette trigger */}
      {!onCmdPaletteOpen && (
        <div className="hidden md:flex flex-1 justify-center">
          <RssTicker />
        </div>
      )}

      {/* Right controls — compact */}
      <div className="flex items-center gap-2">
        <TopBarPricingLink />
        <div className="hidden md:flex">
          <LanguageSwitcher />
        </div>
        <ThemeToggle />

        {/* User avatar */}
        <div className="flex items-center gap-2 ml-1">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium"
            style={{
              background: "var(--accent-subtle)",
              color: "var(--accent-text)",
            }}
            role="img"
            aria-label={session?.user?.name ?? "User"}
          >
            {session?.user?.name?.charAt(0)?.toUpperCase() || "U"}
          </div>
          <div className="hidden sm:block">
            <p
              className="text-[13px] font-medium leading-none"
              style={{ color: "var(--text)" }}
            >
              {session?.user?.name}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}
